/**
 * Grow Logs / Proof Layer — Phase 1 P0 (v3.99ad).
 *
 * Shared utilities + the public list page (/grow-logs).
 *
 * Design contract:
 *   - Live grow logs and Archived logs both surface in the same list, with
 *     distinct pills so collectors can tell instantly which they're seeing.
 *   - Verification levels are surfaced as small mono-spaced pills next to
 *     the title — drives the trust-platform aesthetic without becoming
 *     ornament-y.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Calendar, Sprout, Flower2, Beaker, Wind, CheckCircle2, Archive, Camera, ShieldCheck, AlertTriangle, Heart, User as UserIcon } from "lucide-react";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

// ---------------------------------------------------------------------------
// Shared constants — keep in sync with backend GROW_LOG_* sets.
// ---------------------------------------------------------------------------
export const GROW_LOG_STATUSES = [
  "germinating", "seedling", "vegetative", "flowering",
  "drying", "curing", "completed", "archived",
];
export const GROW_LOG_ENVIRONMENTS = ["indoor", "outdoor", "greenhouse", "mixed"];
export const GROW_LOG_MEDIUMS = ["soil", "coco", "hydro", "living_soil", "rockwool", "aero", "other"];
export const GROW_LOG_NUTRIENT_STYLES = ["organic", "synthetic", "kis", "tlo", "mixed", "other"];

export const STATUS_META = {
  germinating: { label: "Germinating", icon: Sprout, color: "#9BD08C" },
  seedling:    { label: "Seedling",    icon: Sprout, color: "#A6E3A1" },
  vegetative:  { label: "Vegetative",  icon: Leaf,   color: "#7CC9FF" },
  flowering:   { label: "Flowering",   icon: Flower2, color: "#F7C6E0" },
  drying:      { label: "Drying",      icon: Wind,    color: "#E0C28A" },
  curing:      { label: "Curing",      icon: Beaker,  color: "#D8B36C" },
  completed:   { label: "Completed",   icon: CheckCircle2, color: "var(--primary-text)" },
  archived:    { label: "Archived",    icon: Archive,  color: "var(--text-muted)" },
};

export const VERIFICATION_META = {
  // v3.99bi — Renamed so labels make the author/source obvious at a glance.
  // "Self-reported" was unclear about WHO submitted it; "Collector submission"
  // makes the chain of custody clear and pairs with "Breeder submission" for
  // breeder-authored logs.
  self_reported:      { label: "Collector submission",  color: "var(--text-muted)" },
  verified_purchase:  { label: "Verified purchase",     color: "var(--gold)" },
  breeder_verified:   { label: "Breeder submission",    color: "var(--primary-text)" },
  community_supported:{ label: "Community supported",   color: "#7CC9FF" },
  legacy_archive:     { label: "Legacy archive",        color: "#D8B36C" },
};

// ---------------------------------------------------------------------------
// Reusable card — surfaced on /grow-logs, strain pages, breeder pages, etc.
// ---------------------------------------------------------------------------
export function GrowLogCard({ log }) {
  const status = STATUS_META[log.status] || STATUS_META.completed;
  const StatusIcon = status.icon;
  const vmeta = VERIFICATION_META[log.verification_level] || VERIFICATION_META.self_reported;
  const isArchived = log.log_type === "archived";

  return (
    <Link
      to={`/grow-logs/${log.slug || log.id}`}
      className="group bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold-soft)] transition-colors overflow-hidden flex flex-col"
      style={{ borderRadius: 2 }}
      data-testid={`grow-log-card-${log.slug || log.id}`}
    >
      <div className="aspect-[4/3] bg-[var(--surface-2)] relative overflow-hidden">
        {log.cover_image_url ? (
          <img src={log.cover_image_url} alt="" loading="lazy"
               className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Camera className="w-7 h-7 opacity-30" />
          </div>
        )}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em] border backdrop-blur-sm"
             style={{
               background: "rgba(11,11,11,0.6)",
               color: isArchived ? "#D8B36C" : status.color,
               borderColor: isArchived ? "rgba(216,179,108,0.4)" : "var(--border-strong)",
               borderRadius: 2,
             }}>
          {isArchived ? <><Archive className="w-2.5 h-2.5" /> Archive</> : <><StatusIcon className="w-2.5 h-2.5" /> {status.label}</>}
        </div>
        {log.herm_reported && (
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.16em]"
               style={{ background: "rgba(216,147,147,0.18)", color: "#D89393", borderRadius: 2, border: "1px solid rgba(216,147,147,0.5)" }}>
            <AlertTriangle className="w-2.5 h-2.5" /> Herm reported
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col gap-1.5">
        {log.vendor_name && (
          <div className="label-eyebrow text-[9px]">{log.vendor_name}</div>
        )}
        <h3 className="font-serif text-lg tracking-tight line-clamp-2"
            style={{ color: "var(--text)" }}>
          {log.title || `${log.strain_name} grow`}
        </h3>
        <div className="text-xs line-clamp-1" style={{ color: "var(--text-muted)" }}>
          {log.strain_name}
        </div>
        {/* v3.99bi — Surface the grower's display name so collectors instantly
            see whose grow this is. Falls back gracefully when the log was
            seeded without an author name. */}
        {log.user_name && (
          <div className="text-[11px] font-mono inline-flex items-center gap-1 mt-1"
               style={{ color: "var(--text-2)" }}
               data-testid={`grow-log-author-${log.slug || log.id}`}>
            <UserIcon className="w-3 h-3 opacity-70" />
            <span className="truncate">{log.user_name}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-[10px] mt-2 pt-2 border-t border-[var(--border)]">
          <span className="font-mono uppercase tracking-[0.16em]" style={{ color: vmeta.color }}>
            <ShieldCheck className="w-3 h-3 inline -mt-0.5 mr-0.5" />
            {vmeta.label}
          </span>
          <span className="font-mono inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
            <Heart className="w-3 h-3" /> {log.like_count || 0}
          </span>
        </div>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// /grow-logs — public index, filterable.
// ---------------------------------------------------------------------------
export default function GrowLogs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // v3.99bo — broader filter set + smaller-card grid + featured grower
  const [type, setType] = useState("all");                       // all | live | archived
  const [verification, setVerification] = useState("all");
  const [environment, setEnvironment] = useState("all");
  const [medium, setMedium] = useState("all");
  const [seedType, setSeedType] = useState("all");
  const [status, setStatus] = useState("all");
  const [vendor, setVendor] = useState("all");
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const params = { limit: 120 };
    if (type !== "all") params.log_type = type;
    if (verification !== "all") params.verification_level = verification;
    if (vendor !== "all") params.vendor_id = vendor;
    setLoading(true);
    api.get("/grow-logs", { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type, verification, vendor]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = items.filter((l) => {
      if (environment !== "all" && l.environment !== environment) return false;
      if (medium !== "all" && l.medium !== medium) return false;
      if (seedType !== "all" && (l.seed_type || "unknown") !== seedType) return false;
      if (status !== "all" && l.status !== status) return false;
      if (q && ![
        l.title, l.strain_name, l.vendor_name, l.user_name,
        l.phenotype_notes, l.terpene_notes, l.summary,
      ].some((f) => (f || "").toLowerCase().includes(q))) return false;
      return true;
    });
    const verifRank = { breeder_verified: 4, verified_purchase: 3,
                        community_supported: 2, legacy_archive: 1,
                        self_reported: 0 };
    const dateOf = (g) => new Date(g.created_at || 0).getTime();
    switch (sort) {
      case "oldest":   out.sort((a,b) => dateOf(a) - dateOf(b)); break;
      case "likes":    out.sort((a,b) => (b.like_count || 0) - (a.like_count || 0) || dateOf(b) - dateOf(a)); break;
      case "verified": out.sort((a,b) =>
        (verifRank[b.verification_level] ?? 0) - (verifRank[a.verification_level] ?? 0)
        || dateOf(b) - dateOf(a)); break;
      case "flowering":
        out.sort((a,b) => (b.flowering_time_days || 0) - (a.flowering_time_days || 0)); break;
      case "yield":
        out.sort((a,b) => (b.dry_yield_grams || 0) - (a.dry_yield_grams || 0)); break;
      default: out.sort((a,b) => dateOf(b) - dateOf(a));
    }
    return out;
  }, [items, environment, medium, seedType, status, sort, search]);

  // Featured grower — most verified grows in the current result set.
  const featured = useMemo(() => {
    const byUser = new Map();
    items.forEach((l) => {
      const u = l.user_name || l.user_id;
      if (!u) return;
      const cur = byUser.get(u) || { name: u, total: 0, verified: 0, logs: [] };
      cur.total += 1;
      if (["breeder_verified", "verified_purchase", "community_supported"]
            .includes(l.verification_level)) cur.verified += 1;
      cur.logs.push(l);
      byUser.set(u, cur);
    });
    const ranked = Array.from(byUser.values())
      .sort((a, b) => b.verified - a.verified || b.total - a.total);
    return ranked[0] && ranked[0].verified >= 2 ? ranked[0] : null;
  }, [items]);

  const breederOptions = useMemo(() => {
    const m = new Map();
    items.forEach((l) => {
      if (l.vendor_id && l.vendor_name && !m.has(l.vendor_id)) {
        m.set(l.vendor_id, l.vendor_name);
      }
    });
    return Array.from(m.entries());
  }, [items]);

  const counts = useMemo(() => ({
    total: filtered.length,
    live: filtered.filter((l) => l.log_type === "live").length,
    archived: filtered.filter((l) => l.log_type === "archived").length,
  }), [filtered]);

  return (
    <div className="max-w-[1320px] mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="grow-logs-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--gold)" }}>Proof Layer</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-3"
              style={{ color: "var(--text)" }}>
            Grow logs
          </h1>
          <p className="text-base max-w-2xl" style={{ color: "var(--text-2)" }}>
            Verified cultivation evidence from the CannaForge collector network. Live journals and archived grows from
            past pheno hunts feed the Forge Index, breeder trust scores, and collector reputation.
          </p>
        </div>
        <Link to="/grow-logs/new" className="btn-primary inline-flex items-center gap-2 self-start md:self-end"
              data-testid="grow-log-new-cta">
          <Sprout className="w-4 h-4" />
          {user ? "New grow log" : "Sign in to log a grow"}
        </Link>
      </div>

      {/* Featured grower module */}
      {featured && (
        <div className="mb-8 bg-[var(--surface)] border border-[var(--border-strong)] p-4 md:p-5 flex flex-wrap items-center gap-4"
             style={{ borderRadius: 2 }}
             data-testid="featured-grower">
          <div className="label-eyebrow flex items-center gap-1 whitespace-nowrap"
               style={{ color: "var(--gold)" }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Featured grower
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-serif text-xl tracking-tight" style={{ color: "var(--text)" }}>
              {featured.name}
            </div>
            <div className="text-[12px] font-mono mt-0.5" style={{ color: "var(--text-2)" }}>
              <span style={{ color: "var(--primary-text)" }}>{featured.verified}</span> verified
              {" · "}
              <span>{featured.total}</span> total documented grow{featured.total > 1 ? "s" : ""}
            </div>
          </div>
          <div className="flex gap-2 ml-auto">
            {featured.logs.slice(0, 3).map((l) => (
              <Link key={l.id} to={`/grow-logs/${l.slug || l.id}`}
                    className="block w-14 h-14 bg-[var(--surface-2)] border border-[var(--border)] overflow-hidden hover:border-[var(--gold)] transition-colors">
                {l.cover_image_url
                  ? <img src={l.cover_image_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center">
                      <Camera className="w-4 h-4 opacity-30" />
                    </div>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Search + sort */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[180px]">
          <input className="input-field text-xs" placeholder="Search title, strain, breeder, grower, notes…"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 data-testid="gl-search" />
        </div>
        <select className="input-field text-xs !py-2" value={sort}
                onChange={(e) => setSort(e.target.value)} data-testid="gl-sort">
          <option value="newest">Sort: Newest</option>
          <option value="oldest">Oldest</option>
          <option value="likes">Most liked</option>
          <option value="verified">Most verified</option>
          <option value="flowering">Longest flowering</option>
          <option value="yield">Highest yield</option>
        </select>
      </div>

      {/* Dropdown filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {breederOptions.length > 0 && (
          <select className="input-field text-xs !py-2"
                  value={vendor} onChange={(e) => setVendor(e.target.value)}
                  data-testid="gl-vendor">
            <option value="all">All breeders</option>
            {breederOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        )}
        <select className="input-field text-xs !py-2" value={environment}
                onChange={(e) => setEnvironment(e.target.value)} data-testid="gl-environment">
          <option value="all">Any environment</option>
          {GROW_LOG_ENVIRONMENTS.map((v) => (
            <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
          ))}
        </select>
        <select className="input-field text-xs !py-2" value={medium}
                onChange={(e) => setMedium(e.target.value)} data-testid="gl-medium">
          <option value="all">Any medium</option>
          {GROW_LOG_MEDIUMS.map((v) => (
            <option key={v} value={v}>{v.replace(/_/g, " ")}</option>
          ))}
        </select>
        <select className="input-field text-xs !py-2" value={seedType}
                onChange={(e) => setSeedType(e.target.value)} data-testid="gl-seed-type">
          <option value="all">Any seed type</option>
          <option value="regular">Regular</option>
          <option value="feminized">Feminized</option>
          <option value="autoflower">Autoflower</option>
          <option value="unknown">Unknown</option>
        </select>
        <select className="input-field text-xs !py-2" value={status}
                onChange={(e) => setStatus(e.target.value)} data-testid="gl-status">
          <option value="all">Any status</option>
          {GROW_LOG_STATUSES.map((v) => (
            <option key={v} value={v}>{(STATUS_META[v]?.label) || v}</option>
          ))}
        </select>
      </div>

      {/* Type + trust pills */}
      <div className="flex flex-wrap items-center gap-2 mb-6 text-xs">
        <FilterPill label={`All · ${counts.total}`} active={type === "all"} onClick={() => setType("all")} testId="filter-all" />
        <FilterPill label={`Live · ${counts.live}`} active={type === "live"} onClick={() => setType("live")} testId="filter-live" />
        <FilterPill label={`Archive · ${counts.archived}`} active={type === "archived"} onClick={() => setType("archived")} testId="filter-archived" />
        <div className="mx-2 h-4 w-px bg-[var(--border)]" />
        <FilterPill label="Any trust" active={verification === "all"} onClick={() => setVerification("all")} />
        <FilterPill label="Verified purchase" active={verification === "verified_purchase"} onClick={() => setVerification("verified_purchase")} />
        <FilterPill label="Breeder submission" active={verification === "breeder_verified"} onClick={() => setVerification("breeder_verified")} />
        <FilterPill label="Community" active={verification === "community_supported"} onClick={() => setVerification("community_supported")} />
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm" style={{ color: "var(--text-muted)" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[var(--surface)] border border-dashed border-[var(--border-strong)] py-16 px-6 text-center"
             data-testid="grow-logs-empty">
          <Sprout className="w-10 h-10 mx-auto mb-5" style={{ color: "var(--text-muted)" }} />
          <div className="label-eyebrow mb-3" style={{ color: "var(--text-muted)" }}>
            No matches in this view
          </div>
          <div className="font-display tracking-tight text-xl md:text-2xl mb-3" style={{ color: "var(--text)" }}>
            Nothing here under these filters yet.
          </div>
          <Link to="/grow-logs/new" className="btn-outline inline-flex items-center gap-2" data-testid="grow-logs-empty-cta">
            <Sprout className="w-4 h-4" />
            {user ? "Open the new grow flow" : "Sign in to log a grow"}
          </Link>
        </div>
      ) : (
        // v3.99bo — Tighter grid: 2-col mobile, 3 sm, 4 md, 5 lg, 6 xl.
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {filtered.map((l) => <GrowLogCard key={l.id} log={l} />)}
        </div>
      )}
    </div>
  );
}

function FilterPill({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="px-2.5 py-1 font-mono uppercase tracking-[0.16em] text-[10px] border transition-colors"
      style={{
        background: active ? "var(--gold-soft)" : "transparent",
        color: active ? "var(--gold)" : "var(--text-muted)",
        borderColor: active ? "var(--gold)" : "var(--border)",
        borderRadius: 2,
      }}
    >
      {label}
    </button>
  );
}
